"use client";

import NextLink from "next/link";
import { usePathname as useNextPathname, useRouter as useNextRouter } from "next/navigation";
import { useLocale } from "next-intl";
import type { ComponentProps } from "react";

import { normalizeLocale } from "@/lib/i18n/locales";
import { getPathnameLocale, localizePathname } from "@/lib/i18n/pathnames";

type Href = ComponentProps<typeof NextLink>["href"];
interface RouterOptions {
  locale?: string;
  scroll?: boolean;
}

const localizeStringHref = (href: string, locale: string) => {
  const normalizedLocale = normalizeLocale(locale);

  if (!href.startsWith("/") || href.startsWith("/api/")) {
    return href;
  }

  return localizePathname(href, normalizedLocale);
};

const localizeHref = (href: Href, locale: string): Href => {
  if (typeof href !== "string") {
    return href;
  }

  return localizeStringHref(href, locale);
};

const stripLocale = (pathname: string) => {
  const locale = getPathnameLocale(pathname);

  return locale === null ? pathname : pathname.replace(`/${locale}`, "") || "/";
};

export const Link = ({ href, ...props }: ComponentProps<typeof NextLink>) => {
  const locale = useLocale();

  return <NextLink href={localizeHref(href, locale)} {...props} />;
};

export const usePathname = () => stripLocale(useNextPathname());

export const useRouter = () => {
  const locale = normalizeLocale(useLocale());
  const router = useNextRouter();

  return {
    ...router,
    push: (href: string, options?: RouterOptions) => {
      router.push(localizeStringHref(href, options?.locale ?? locale), options);
    },
    replace: (href: string, options?: RouterOptions) => {
      router.replace(localizeStringHref(href, options?.locale ?? locale), options);
    },
  };
};
